import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import * as CookieConsent from '@planone/shared/feature-cookie-consent';
import { DocumentRef } from '@planone/shared/util-browser-api';
import { REQUEST } from '@planone/shared/util-ssr';
import { delay, filter, of, pairwise, startWith, switchMap, tap, withLatestFrom } from 'rxjs';

import * as GoogleAnalyticsActions from './google-analytics.actions';
import { GoogleAnalyticsConfiguration } from './google-analytics.models';
import * as GoogleAnalyticsReducer from './google-analytics.reducer';
import * as GoogleAnalyticsSelectors from './google-analytics.selectors';

import { GoogleAnalyticsService } from '../service/google-analytics.service';
import { GOOGLE_ANALYTICS_CONFIGURATION } from '../token/google-analytics.tokens';

@Injectable()
export class GoogleAnalyticsEffects {
  initialize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoogleAnalyticsActions.initialize),
        tap((action) => {
          this.service.initialize(action);
        })
      ),
    { dispatch: false }
  );

  push$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoogleAnalyticsActions.push),
        filter((action) => !!action.enriched),
        withLatestFrom(this.store.pipe(select(CookieConsent.getAcceptanceStatus))),
        delay(0),
        withLatestFrom(this.store.pipe(select(GoogleAnalyticsSelectors.getPreviousUrl))),
        tap(([[action, cookieConsentStatus], pageReferrer]) => {
          let pageTitle = action['page_title'] ?? this.title.getTitle();

          if (action.title_context) {
            const pageTitleSections = pageTitle.split(' | ');

            pageTitleSections[pageTitleSections.length - 1] =
              action.title_context + ' | ' + pageTitleSections[pageTitleSections.length - 1];
            pageTitle = pageTitleSections.join(' | ');
          }

          const modifiableAction = { ...action };

          if (Object.keys(modifiableAction).includes('title_context')) {
            delete modifiableAction.title_context;
          }

          this.service.push({
            ...action,
            cookie_consent: cookieConsentStatus ? 'yes' : 'no',
            traffic_type: !this.configuration.production || action['exclude_analytics'] ? 'internal' : 'public',
            ...(action?.event === 'pageview' && pageReferrer && { page_referrer: pageReferrer }),
            ...{ page_title: pageTitle },
          });
        })
      ),
    { dispatch: false }
  );

  routing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      startWith(undefined),
      pairwise(),
      switchMap((actions) => {
        const previousRoute = (<any>actions[0])?.payload.routerState;
        const currentUrlObject = new URL(this.documentRef.nativeDocument.URL ?? this.request.url);
        let previousUrl = undefined;

        if (previousRoute) {
          previousUrl = currentUrlObject.origin + this.baseHref.slice(0, -1) + previousRoute.url;
        }

        return of(GoogleAnalyticsActions.setPreviousUrl({ url: previousUrl }));
      })
    )
  );

  processEventQueue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CookieConsent.initialize, CookieConsent.changed),
        filter((action) => action.status !== undefined),
        delay(0),
        tap(() => this.service.processQueue())
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(GOOGLE_ANALYTICS_CONFIGURATION) private configuration: GoogleAnalyticsConfiguration,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Optional() @Inject(REQUEST) private request: Request,
    private readonly actions$: Actions,
    private store: Store<GoogleAnalyticsReducer.GoogleAnalyticsState>,
    private service: GoogleAnalyticsService,
    private documentRef: DocumentRef,
    private title: Title
  ) {}
}
